import dayjs from "dayjs";

export async function loadReportTransactions(fetchPage, range) {
  const rows = [];
  let expected;
  for (let page = 1; ; page += 1) {
    const response = await fetchPage({ ...range, page, limit: 100 });
    if (!response.success || !Array.isArray(response.data) || !response.pagination) {
      throw new Error("Unable to load the complete report. Please try again.");
    }
    if (expected === undefined) expected = response.pagination.totalItems;
    if (expected !== response.pagination.totalItems) {
      throw new Error("Payments changed while loading. Please refresh the report.");
    }
    rows.push(...response.data);
    if (page >= response.pagination.totalPages) break;
    if (!response.data.length) throw new Error("Incomplete report. Please try again.");
  }
  if (rows.length !== expected || new Set(rows.map((r) => r._id)).size !== rows.length) {
    throw new Error("Payments changed while loading. Please refresh the report.");
  }
  return rows;
}

export function summarizeReport(rows) {
  const result = {};
  ["CREDIT", "DEBIT"].forEach((type) => {
    const entries = rows.filter((row) => row.transactionType === type);
    result[type] = {
      count: entries.length,
      amount: entries.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      modes: ["ONLINE", "CASH"].map((mode) => ({
        label: mode === "ONLINE" ? "Online" : "Cash",
        amount: entries
          .filter((row) => row.method === mode)
          .reduce((sum, row) => sum + Number(row.amount || 0), 0),
        count: entries.filter((row) => row.method === mode).length,
      })),
    };
  });
  return result;
}

export const reportMoney = (value) =>
  Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export function reportInvoiceType(row) {
  const invoiceType = String(row.financeId?.invoiceType || "").toUpperCase();
  if (["ACTIVATION", "RENEWAL", "ADDITIONAL"].includes(invoiceType)) return invoiceType;
  // Historical transactions may have a payment label without a linked invoice.
  if (row.paymentType === "Class Activation Payment") return "ACTIVATION";
  if (row.paymentType === "Class Renewal Payment") return "RENEWAL";
  return "OTHER";
}

export function calculateReportMargin(rows) {
  const credits = rows.filter((row) => row.transactionType === "CREDIT");
  if (!credits.every((row) => row.reportTutorPayable != null && Number.isFinite(Number(row.reportTutorPayable)))) {
    return null;
  }
  const revenue = credits.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const tutorPayable = credits.reduce((sum, row) => sum + Number(row.reportTutorPayable || 0), 0);
  const amount = revenue - tutorPayable;
  return { revenue, tutorPayable, amount, percentage: revenue > 0 ? amount / revenue * 100 : null };
}

export async function exportFinanceReport({ rows, range, dummy = false }) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const summary = summarizeReport(rows);
  const margin = calculateReportMargin(rows);
  const tutorPayable = margin?.tutorPayable;
  const invoiceSplit = ["ACTIVATION", "RENEWAL", "OTHER"].map((type) => {
    const entries = rows.filter((row) => row.transactionType === "CREDIT" && reportInvoiceType(row) === type);
    return { type, count: entries.length, amount: entries.reduce((sum, row) => sum + Number(row.amount || 0), 0) };
  });
  const C = { navy: "#172B4D", blue: "#5264E8", teal: "#13A89E", muted: "#6B7890", light: "#F3F6FB" };
  const dateLabel = `${dayjs(range.fromdate).format("DD MMM YYYY")} - ${dayjs(range.todate).format("DD MMM YYYY")}`;
  const txt = (value, x, y, size = 10, color = C.navy, bold = false) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    pdf.text(String(value), x, y);
  };
  const box = (x, y, w, h) => {
    pdf.setFillColor(C.light);
    pdf.roundedRect(x, y, w, h, 3, 3, "F");
  };
  const header = (title, subtitle) => {
    pdf.setFillColor(C.navy);
    pdf.rect(0, 0, 210, 43, "F");
    txt("EDUSHALA", 15, 14, 12, "#FFFFFF", true);
    txt(dummy ? "FINANCE / CLIENT PREVIEW" : "FINANCE / REPORT", 120, 14, 8, "#BAC7E0");
    txt(title, 15, 28, 23, "#FFFFFF", true);
    txt(`${dateLabel}  |  ${subtitle}`, 15, 37, 8, "#D7E0F2");
  };
  const card = (x, y, w, label, value, note, color = C.blue) => {
    box(x, y, w, 30);
    txt(label, x + 4, y + 7, 8, C.muted, true);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    const size = Math.min(18, 18 * (w - 8) / Math.max(pdf.getTextWidth(String(value)), 1));
    txt(value, x + 4, y + 18, size, color, true);
    txt(note, x + 4, y + 25, 7.5, C.muted);
  };
  const heading = (title, y) => txt(title, 15, y, 13, C.navy, true);
  // Fill each slice as one curved path so PDF viewers do not show triangle seams.
  const pieSlice = (cx, cy, radius, start, end) => {
    if (end <= start) return;
    if (end - start >= 2 * Math.PI - 1e-10) {
      pdf.circle(cx, cy, radius, "F");
      return;
    }
    pdf.moveTo(cx, cy);
    pdf.lineTo(cx + radius * Math.cos(start), cy + radius * Math.sin(start));
    const segments = Math.ceil((end - start) / (Math.PI / 2));
    const step = (end - start) / segments;
    for (let i = 0; i < segments; i += 1) {
      const a = start + i * step;
      const b = start + (i + 1) * step;
      const k = (4 / 3) * Math.tan((b - a) / 4);
      pdf.curveTo(
        cx + radius * (Math.cos(a) - k * Math.sin(a)),
        cy + radius * (Math.sin(a) + k * Math.cos(a)),
        cx + radius * (Math.cos(b) + k * Math.sin(b)),
        cy + radius * (Math.sin(b) - k * Math.cos(b)),
        cx + radius * Math.cos(b),
        cy + radius * Math.sin(b)
      );
    }
    pdf.close();
    pdf.fill();
  };
  const invoiceStyles = {
    ACTIVATION: { label: "Activation", color: C.blue, background: "#E9EDFF" },
    RENEWAL: { label: "Renewal", color: "#09877F", background: "#DEF5EF" },
    OTHER: { label: "Other / N.A.", color: C.muted, background: "#E9EDF2" },
  };
  const invoiceChart = () => {
    box(15, 126, 180, 66);
    txt("ACTIVATION / RENEWAL", 20, 135, 9, C.navy, true);
    txt("Credit received - split by amount", 20, 141, 8, C.muted);
    const total = summary.CREDIT.amount;
    if (!total) { txt("No credit payments in this period", 20, 158, 9, C.muted); return; }
    let angle = -Math.PI / 2;
    invoiceSplit.forEach((group, i) => {
      const style = invoiceStyles[group.type];
      const end = angle + 2 * Math.PI * group.amount / total;
      pdf.setFillColor(style.color);
      pieSlice(42, 163, 17, angle, end);
      angle = end;
      const y = 148 + i * 13;
      pdf.circle(77, y - 1, 1.5, "F");
      txt(`${style.label}  /  ${(group.amount / total * 100).toFixed(1)}%`, 82, y, 9, style.color, true);
      txt(`INR ${reportMoney(group.amount)}  |  ${group.count} payments`, 82, y + 5, 8, C.muted);
    });
    txt("Other / N.A. includes other fees and payments without an invoice type.", 20, 188, 7, C.muted);
  };
  const table = (y, labels, entries, widths) => {
    let x = 15;
    pdf.setFillColor(C.navy);
    pdf.rect(15, y, 180, 10, "F");
    labels.forEach((label, i) => { txt(label, x + 2, y + 6.5, 7.5, "#FFFFFF", true); x += widths[i]; });
    y += 10;
    entries.forEach((entry, index) => {
      if (index % 2 === 0) { pdf.setFillColor(C.light); pdf.rect(15, y, 180, 10, "F"); }
      x = 15;
      entry.forEach((value, i) => { txt(value, x + 2, y + 6.5, 8, value === "PAID" ? C.teal : C.navy); x += widths[i]; });
      y += 10;
    });
    return y;
  };
  const modeChart = (left, data, label) => {
    box(left, 140, 86, 68);
    txt(label, left + 5, 148, 9, C.navy, true);
    if (!data.amount) { txt("No payments in this period", left + 6, 176, 9, C.muted); return; }
    const cx = left + 24, cy = 174, radius = 18;
    let angle = -Math.PI / 2;
    data.modes.forEach((mode, i) => {
      const color = i === 0 ? C.blue : C.teal;
      const end = angle + 2 * Math.PI * mode.amount / data.amount;
      pdf.setFillColor(color);
      pieSlice(cx, cy, radius, angle, end);
      angle = end;
      const y = 165 + i * 20;
      pdf.circle(left + 47, y - 1, 1.4, "F");
      txt(`${mode.label} / ${(mode.amount / data.amount * 100).toFixed(1)}%`, left + 51, y, 8);
      txt(reportMoney(mode.amount), left + 51, y + 5, 8, C.muted);
      txt(`${mode.count} payments`, left + 51, y + 9, 7, C.muted);
    });
  };
  const gmPercent = margin?.percentage ?? null;
  header("Finance report", "Overview");
  card(15, 51, 86, "TOTAL CREDIT RECEIVED", reportMoney(summary.CREDIT.amount), `${summary.CREDIT.count} successful payments`, C.teal);
  card(109, 51, 86, "TOTAL DEBIT PAID", reportMoney(summary.DEBIT.amount), `${summary.DEBIT.count} completed payouts`);
  card(15, 86, 86, "NET CASH MOVEMENT", reportMoney(summary.CREDIT.amount - summary.DEBIT.amount), "Credit received less debit paid");
  card(109, 86, 86, "GROSS MARGIN (GM)", margin ? `${reportMoney(margin.amount)}${gmPercent !== null ? ` / ${gmPercent.toFixed(2)}%` : ""}` : "Unavailable", margin ? `Tutor payable on credit: INR ${reportMoney(tutorPayable)}` : "Tutor payable details missing for some credits", C.teal);
  heading("Payment mode split", 128);
  txt("Based on completed payments only", 15, 134, 8, C.muted);
  modeChart(15, summary.CREDIT, "CREDIT RECEIVED");
  modeChart(109, summary.DEBIT, "DEBIT PAID");
  heading("At a glance", 220);
  table(226, ["METRIC", "CREDIT", "DEBIT"], [
    ["Total no. of payments", String(summary.CREDIT.count), String(summary.DEBIT.count)],
    ["Total amount", reportMoney(summary.CREDIT.amount), reportMoney(summary.DEBIT.amount)],
    ["Payment status", summary.CREDIT.count ? "Received" : "No payments", summary.DEBIT.count ? "Paid" : "No payments"],
  ], [64, 58, 58]);
  txt("GM = credit received - tutor payable for that credit (including unpaid tutor amounts).", 15, 272, 8, C.muted);

  for (const type of ["CREDIT", "DEBIT"]) {
    const entries = rows.filter((row) => row.transactionType === type);
    const data = summary[type];
    let y;
    let pageIndex = 0;
    const listPage = () => {
      pdf.addPage();
      header(type === "CREDIT" ? "Credit collections" : "Debit & margin", type === "CREDIT" ? "Payments received" : "Payments made");
      card(15, 51, 56, "TOTAL LIST ENTRIES", String(data.count), "Selected report period");
      card(77, 51, 56, type === "CREDIT" ? "PAYMENTS RECEIVED" : "COMPLETED PAYOUTS", String(data.count), "Completed payments", C.teal);
      card(139, 51, 56, type === "CREDIT" ? "AMOUNT RECEIVED" : "TOTAL DEBIT PAID", reportMoney(data.amount), "All amounts in INR", C.teal);
      if (pageIndex === 0) {
        heading(type === "CREDIT" ? "Credit status summary" : "Debit status summary", 94);
        table(100, ["STATUS", "NO. OF ENTRIES", "AMOUNT"], [["PAID", String(data.count), reportMoney(data.amount)]], [65, 55, 60]);
        if (type === "CREDIT") invoiceChart();
        heading(type === "CREDIT" ? "Credit transaction list" : "Debit payment list", type === "CREDIT" ? 202 : 133);
        y = type === "CREDIT" ? 208 : 139;
      } else {
        heading("Transaction list (continued)", 94);
        y = 100;
      }
      if (type === "CREDIT") {
        table(y, ["DATE", "PARENT NAME", "INVOICE TYPE", "METHOD", "AMOUNT (INR)", "STATUS"], [], [24, 48, 27, 22, 37, 22]);
      } else {
        table(y, ["DATE", "TUTOR NAME", "METHOD", "AMOUNT (INR)", "STATUS"], [], [24, 75, 25, 34, 22]);
      }
      y += 10;
      txt("Completed payments only. Pending invoices and unpaid requests are excluded.", 15, 271, 8, C.muted);
      pageIndex += 1;
    };
    listPage();
    if (!entries.length) txt("No payments in this period.", 17, y + 7, 9, C.muted);
    entries.forEach((row, index) => {
      const user = type === "DEBIT" ? row.tutorId : row.parentId;
      const cellWidth = type === "CREDIT" ? 44 : 71;
      const phone = user?.phone == null ? "--" : String(user.phone);
      const studentName = Array.isArray(row.studentNames)
        ? row.studentNames.filter(Boolean).join(", ") || "--"
        : "--";
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      const name = pdf.splitTextToSize(user?.fullName || "Name unavailable", cellWidth);
      pdf.setFontSize(7);
      const details = (type === "CREDIT"
        ? [`Student: ${studentName}`, `Phone: ${phone}`]
        : [`Phone: ${phone}`]
      ).flatMap((value) => pdf.splitTextToSize(value, cellWidth));
      const height = Math.max(18, name.length * 4 + details.length * 3.5 + 6);
      if (y + height > 263) listPage();
      if (index % 2 === 0) { pdf.setFillColor(C.light); pdf.rect(15, y, 180, height, "F"); }
      txt(row.effectivePaymentDate ? dayjs(row.effectivePaymentDate).format("DD MMM YY") : "--", 17, y + 5, 8);
      name.forEach((line, lineIndex) => pdf.text(line, 41, y + 5 + lineIndex * 4));
      pdf.setFontSize(7);
      pdf.setTextColor(C.muted);
      let detailY = y + 5 + name.length * 4;
      details.forEach((line) => {
        pdf.text(line, 41, detailY);
        detailY += 3.5;
      });
      if (type === "CREDIT") {
        const invoiceStyle = invoiceStyles[reportInvoiceType(row)];
        pdf.setFillColor(invoiceStyle.background);
        pdf.roundedRect(88, y + 1, 25, 7, 1.5, 1.5, "F");
        txt(invoiceStyle.label, 90, y + 5.5, 7, invoiceStyle.color, true);
        txt(row.method === "ONLINE" ? "ONLINE" : row.method === "CASH" ? "CASH" : "--", 116, y + 5, 8);
        txt(reportMoney(row.amount), 138, y + 5, 8);
        txt("PAID", 175, y + 5, 8, C.teal);
      } else {
        txt(row.method === "ONLINE" ? "ONLINE" : row.method === "CASH" ? "CASH" : "--", 116, y + 5, 8);
        txt(reportMoney(row.amount), 141, y + 5, 8);
        txt("PAID", 175, y + 5, 8, C.teal);
      }
      y += height;
    });
  }
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    pdf.setPage(p);
    pdf.setDrawColor("#DCE3ED");
    pdf.line(15, 279, 195, 279);
    txt(dummy ? "DUMMY DATA | Client preview | All amounts in INR" : "Edushala | Finance report | All amounts in INR", 15, 286, 8, C.muted);
    txt(`${p} / ${pages}`, 184, 286, 8, C.muted);
  }
  pdf.setProperties({ title: "Edushala Finance Report", author: "Edushala", subject: dateLabel });
  pdf.save(`Edushala-Finance-${range.fromdate}-to-${range.todate}${dummy ? "-Demo" : ""}.pdf`);
  return pdf;
}
